
/**
 * @typedef IBookSortFilterSet
 * @memberof Models
 * @property {string} [sortBy] - the sorting selection 
 * @property {string} [titleSearchTerm] - the search term for the title
 * @property {boolean} [startsWithFlag] - flag to indicate if the search term should be at the start of the title or anywhere in the title
 * @property {string} [seriesFilter] - the series to filter on  
 * @property {string} [authorFilter] - the author to filter on
 * @property {string} [narratorFilter] - the narrator to filter on  
 * @property {string} [typeSelect] - the type of book to filter on
 * @property {boolean} [haveRead] - flag to indicate if the book has been read
 * @property {Array<string>} [genres] - the genres to filter on
 * @property {boolean} [addedByMe] - flag to indicate if the book was added by the current user
 * @property {boolean} [readByMe] - flag to indicate if the book was read by the current user
 * @property {boolean} [notReadByMe] - flag to indicate if the book was not read by the current user
 * @property {boolean} [recommendedForMe] - flag to indicate if the book was recommended for the current user
 * @property {boolean} [recommendedByMe] - flag to indicate if the book was recommended by the current user
 * @property {boolean} [imInterestedIn] - flag to indicate if the current user is interested in the book
 */
export type IBookSortFilterSet = {
  sortBy?: string;   // the sorting selection
  sortDirection?: string ; // the sorting direction
  // Filtering options
  titleSearchTerm?: string;
  startsWithFlag?: boolean;
  seriesFilter?: string;
  authorFilter?: string;
  narratorFilter?: string;
  typeSelect?: string;
  haveRead?: boolean | null;
  genres?: Array<string> | null;
  addedByMe?: boolean | null;
  notAddedByMe?: boolean | null;
  readByMe?: boolean | null;
  notReadByMe?: boolean | null;
  recommendedForMe?: boolean | null;
  recommendedByMe?: boolean | null;
  imInterestedIn?: boolean | null;
  sortFilterIsOpen?: boolean | null; // flag to indicate if the sort and filter dialog is open
}


/**
 * @class BookSortFilterSet
 * @memberof Book
 * @classdesc The BookSortFilterSet class is used to store the sort and filter settings for the book list
 * @implements {IBookSortFilterSet}
 * @param {IBookSortFilterSet} sfSet - The sort and filter settings to initialize the class
 */
export class BookSortFilterSet implements IBookSortFilterSet {
  sortBy?: string;   // the sorting selection
  sortDirection?: string; // the sorting direction
  // Filtering options
  titleSearchTerm?: string;
  startsWithFlag?: boolean;
  seriesFilter?: string;
  authorFilter?: string;
  narratorFilter?: string;
  typeSelect?: string;
  haveRead?: boolean | null;
  genres?: Array<string> | null;
  addedByMe?: boolean | null;
  notAddedByMe?: boolean | null;
  readByMe?: boolean | null;
  notReadByMe?: boolean | null;
  recommendedForMe?: boolean | null;
  recommendedByMe?: boolean | null;
  imInterestedIn?: boolean | null;
  sortFilterIsOpen?: boolean | null;

  constructor(sfSet: IBookSortFilterSet) {
    this.sortBy = sfSet.sortBy || 'title';
    this.sortDirection = sfSet.sortDirection || 'asc';
    // Filtering options
    this.titleSearchTerm = sfSet.titleSearchTerm || '';
    this.startsWithFlag = sfSet.startsWithFlag || false;
    this.seriesFilter = sfSet.seriesFilter || '-- Select a Series --';
    this.authorFilter = sfSet.authorFilter || '-- Select an Author --';
    this.narratorFilter = sfSet.narratorFilter || '-- Select a Narrator --';
    this.typeSelect = sfSet.typeSelect || 'Both';
    this.haveRead = sfSet.haveRead || null;
    this.genres = sfSet.genres || [];
    this.addedByMe = sfSet.addedByMe || false;
    this.notAddedByMe = sfSet.notAddedByMe || false;
    this.readByMe = sfSet.readByMe || false;
    this.notReadByMe = sfSet.notReadByMe || false;
    this.recommendedForMe = sfSet.recommendedForMe || false;
    this.recommendedByMe = sfSet.recommendedByMe || false;
    this.imInterestedIn = sfSet.imInterestedIn || false;
    this.sortFilterIsOpen = sfSet.sortFilterIsOpen? sfSet.sortFilterIsOpen : false; // flag to indicate if the sort and filter dialog is open

  }
}

